import { type IAPIResponse, type IGroup } from "@/types";
import type { MediworksSourceFilterBody } from "@/types/mediworksSourceFilter";
import { baseApi } from "@/core/api/baseApi";
import { toUniqueIdNamePairs, type IdName } from "@/shared/lib/idName";

export interface IGroupParams {
  name?: string;
  clientId?: number;
  employerId?: number;
  isActive?: string;
  pageNo?: number;
  pageSize?: number;
}

interface IGlobalSearchParams {
  query: string;
  pageNo?: number;
  pageSize?: number;
}

/** `groupId` narrows the list to that single group. */
export interface GroupIdNameParams {
  clientId: number;
  employerId: number;
  groupId?: number;
}

export const groupsApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getGroups: build.query<IAPIResponse<IGroup>, IGroupParams>({
      query: (body) => ({
        url: `/mediworks/groups`,
        method: "POST",
        body,
      }),
    }),
    getGroupsBySource: build.query<
      IAPIResponse<IGroup>,
      { entityId: number; body: MediworksSourceFilterBody }
    >({
      query: ({ entityId, body }) => ({
        url: `mediworks/group/${entityId}`,
        method: "GET",
        body,
      }),
      transformResponse: (response: {
        data?: IGroup | IGroup[];
        totalCount?: number;
        [key: string]: unknown;
      }): IAPIResponse<IGroup> => {
        const data = response.data;
        const list = Array.isArray(data) ? data : data ? [data] : [];
        return {
          data: list,
          totalCount: response.totalCount ?? list.length,
          pageNo: 1,
          pageSize: list.length || 10,
          currentPage: 1,
          totalPages: list.length ? 1 : 0,
        };
      },
    }),
    searchGlobalGroups: build.query<IAPIResponse<IGroup>, IGlobalSearchParams>({
      query: ({ query, pageNo = 1, pageSize = 100 }) => ({
        url: `/mediworks/groups/global-search`,
        method: "GET",
        params: { query, pageNo, pageSize },
      }),
    }),
    listGroups: build.query<IGroup[], void>({
      query: () => ({
        url: `/mediworks/groups/fetch-id-name`,
        method: "GET",
      }),
    }),
    /**
     * The groups under one employer, id and name only.
     *
     * Distinct from `listGroups`, which is every group on the platform: a
     * caller matching a name against siblings needs the handful under the
     * scope it is looking at, not thousands of unrelated ones.
     */
    getGroupsIdName: build.query<IdName[], GroupIdNameParams>({
      query: ({ clientId, employerId, groupId }) => ({
        url: `/mediworks/groups/id-name`,
        method: "GET",
        params: { clientId, employerId, ...(groupId != null && { groupId }) },
      }),
      transformResponse: (r: unknown) => toUniqueIdNamePairs(r, "groups", "groupId"),
    }),
    /**
     * The groups under several employers, id and name only, merged by id — one id-name call per
     * distinct employer. The platform-wide `fetch-id-name` list is keyed by NAME and collapses
     * same-named rows into one, so it cannot be used to name an id; ids under an employer never collide.
     */
    getGroupsIdNameForScopes: build.query<IdName[], { clientId: number; employerId: number }[]>({
      queryFn: async (scopes, _api, _extra, fetchWithBQ) => {
        const unique = new Map<string, { clientId: number; employerId: number }>();
        for (const { clientId, employerId } of scopes) {
          const key = `${clientId}:${employerId}`;
          if (!unique.has(key)) unique.set(key, { clientId, employerId });
        }
        // Every employer at once rather than one after another; merged in the order asked, so the
        // first employer still wins an id two of them share.
        const results = await Promise.all(
          [...unique.values()].map(({ clientId, employerId }) =>
            fetchWithBQ({
              url: `/mediworks/groups/id-name`,
              method: "GET",
              params: { clientId, employerId },
            })
          )
        );
        const byId = new Map<number, IdName>();
        for (const res of results) {
          // One employer failing must not blank every other employer's names.
          if (res.error) continue;
          for (const row of toUniqueIdNamePairs(res.data, "groups", "groupId"))
            if (!byId.has(row.id)) byId.set(row.id, row);
        }
        return { data: [...byId.values()] };
      },
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupsBySourceQuery,
  useSearchGlobalGroupsQuery,
  useListGroupsQuery,
  useGetGroupsIdNameQuery,
  useGetGroupsIdNameForScopesQuery,
} = groupsApi;
